import { z } from "zod";
import { FormSubmitCallback } from "../../../components/Form";
import { FieldProps } from "react-final-form";
import { FormBase, FormBaseProps } from "../../../components/Form/FormBase";
import InputField from "../../../components/Form/InputField";
import SelectField from "../../../components/Form/SelectField";
import { useEffect, useState } from "react";

const STAFF_FORM_FIELDS: FieldProps<string, any>[] = [
  {
    name: "firstName",
    label: "First Name",
    component: InputField,
    required: true,
    fullWidth: true,
    grid: {
      xs: 6,
    },
  },
  {
    name: "middleName",
    label: "Middle Name",
    component: InputField,
    required: false,
    fullWidth: true,
    grid: {
      xs: 6,
    },
  },
  {
    name: "lastName",
    label: "Last Name",
    component: InputField,
    required: true,
    fullWidth: true,
    grid: {
      xs: 6,
    },
  },
  {
    name: "roleType",
    label: "User Role / Type",
    component: SelectField,
    required: true,
    fullWidth: true,
    grid: {
      xs: 6,
    }
  },
  {
    name: "jobTitle",
    label: "Job Title",
    component: InputField,
    required: false,
    fullWidth: true,
    grid: {
      xs: 12,
    },
  },
  {
    name: "email",
    label: "Email",
    component: InputField,
    required: true,
    fullWidth: true,
    grid: {
      xs: 12,
    },
  }
];

const roleTypeSchema = z.union([z.literal("ADMIN"), z.literal("STANDARD"), z.literal("OFFICE ADMIN"), z.literal("SUPER ADMIN"), z.literal("SUPPORT ADMIN"), z.null(), z.undefined()]);

const performValidationSchema = z.object({
  firstName: z.string().trim().nonempty().max(255),
  middleName: z.string().trim().max(255).nullish(),
  lastName: z.string().trim().nonempty().max(255),
  jobTitle: z.string().trim().max(255).nullish(),
  email: z.string().nonempty().email(),
  roleType: roleTypeSchema.default("STANDARD")
});

export type StaffFormSchema = z.infer<typeof performValidationSchema>;

type FormState = {
  fields: FieldProps<string, any>[]
}

export type StaffRole = {
  name: string,
  value: string,
  default: boolean
}

export type StaffFormProps = {
  roles: StaffRole[]
  rolesLoading: boolean,
  initialValues: StaffFormSchema | null | undefined,
  onSubmit: (values: StaffFormSchema) => Promise<ReturnType<FormSubmitCallback>>
  onCancel: () => void
} & Pick<FormBaseProps<StaffFormSchema>, "initialValues" | "readonly">

export default function StaffForm({ roles, rolesLoading = true, initialValues, readonly, onSubmit, onCancel }: StaffFormProps) {

  const [formState, setFormState] = useState<FormState>({
    fields: STAFF_FORM_FIELDS
  });

  useEffect(() => {
    const fields = STAFF_FORM_FIELDS.map((field) => {
      if (field.name !== "roleType") return field;
      return {
        ...field,
        loading: rolesLoading,
        options: roles.map((role) => ({
          label: role.name,
          value: role.value,
          isDefault: role.default,
          disabled: role.value == "SUPER ADMIN"
        })),
      }
    });

    setFormState(prevState => ({
      ...prevState,
      fields
    }))
  }, [roles]);
  const EditForm = FormBase<StaffFormSchema>({
    fields: formState.fields,
    initialValues: initialValues ?? {},
    validationSchema: performValidationSchema,
    buttonSubmitLabel: "Save",
    buttonCancelLabel: "Cancel",
    readonly,
    onSubmit: onSubmit,
    onCancel: onCancel
  })

  return EditForm;
}